import { useState } from "react";
import { useLocation } from "react-router-dom";
import { PhoneCall, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Notice } from "@/components/site/Bits";

const TIMES = ["As soon as possible", "Morning (9am to 12pm)", "Afternoon (12pm to 5pm)",
               "Evening (after 5pm)"];

const field =
  "w-full rounded-md border border-pg-border bg-white px-3 py-3 text-base text-pg-text outline-none transition-colors focus:border-pg-primary sm:text-sm";

/** Name, number, best time to call. For anyone who would rather be rung than fill in a wizard. */
export const QuickEnquiry = () => {
  const { pathname } = useLocation();
  const [form, setForm] = useState({ name: "", phone: "", best_time: TIMES[0] });
  const [state, setState] = useState({ loading: false, error: "", done: null });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const ready = form.name.trim().length > 1 && form.phone.replace(/\D/g, "").length >= 10;

  const submit = async (event) => {
    event.preventDefault();
    setState({ loading: true, error: "", done: null });
    try {
      const { data } = await api.post("/quick-enquiry", { ...form, landing_page: pathname });
      setState({ loading: false, error: "", done: data });
    } catch (error) {
      setState({ loading: false, error: apiError(error), done: null });
    }
  };

  if (state.done) {
    return (
      <div className="rounded-xl border border-pg-primary/30 bg-pg-pale p-5" data-testid="quick-enquiry-done">
        <p className="flex items-start gap-2.5 text-sm font-semibold text-pg-dark">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-pg-primary" />
          {state.done.message}
        </p>
        <p className="mt-2 pl-8 text-[13px] text-pg-muted sm:text-xs">
          Your reference is {state.done.reference}. Nothing else is needed from you.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quick-enquiry"
      className="rounded-xl border border-pg-border bg-white p-5 shadow-sm"
    >
      <p className="flex items-center gap-2 text-sm font-bold text-pg-dark">
        <PhoneCall className="h-4 w-4 text-pg-primary" />
        In a hurry? We will ring you
      </p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-pg-muted sm:text-sm">
        Leave your name and number and one of us will call you back. No forms, no email address.
      </p>
      <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
        <label className="block">
          <span className="sr-only">Your name</span>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Your name"
            autoComplete="name"
            data-testid="quick-enquiry-name"
            className={field}
          />
        </label>
        <label className="block">
          <span className="sr-only">Telephone number</span>
          <input
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="Telephone number"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            data-testid="quick-enquiry-phone"
            className={field}
          />
        </label>
        <label className="block">
          <span className="sr-only">Best time to call</span>
          <select
            value={form.best_time}
            onChange={(e) => set("best_time", e.target.value)}
            data-testid="quick-enquiry-time"
            className={field}
          >
            {TIMES.map((time) => <option key={time} value={time}>{time}</option>)}
          </select>
        </label>
      </div>
      {state.error && (
        <div className="mt-3">
          <Notice tone="error" testId="quick-enquiry-error">{state.error}</Notice>
        </div>
      )}
      <button
        type="submit"
        disabled={!ready || state.loading}
        data-testid="quick-enquiry-submit"
        className="mt-3 flex min-h-[52px] w-full items-center justify-center rounded-md bg-pg-primary px-5 text-[15px] font-semibold text-white transition-colors hover:bg-pg-dark disabled:opacity-50 sm:min-h-0 sm:w-auto sm:py-3 sm:text-sm"
      >
        {state.loading ? "Sending…" : "Request a callback"}
      </button>
    </form>
  );
};

export default QuickEnquiry;
